/**
 * apps/web/src/components/ui/Button.tsx
 *
 * Button primitive on HeroUI. It renders a native <button> styled with
 * HeroUI's own `buttonVariants` recipe, so the visual (fill, radius, motion,
 * focus, pressed state) comes straight from the library and reads the brand
 * tokens (--accent, --danger, --default) that index.css maps to glarno.
 *
 * We keep a native element rather than the <Button> component so the full
 * DOM prop surface (onClick, disabled, type, form, aria-*, data-*) passes
 * through untouched - the ~32 consuming files change nothing.
 *
 * Variant map -> HeroUI recipe variants:
 *   primary   -> primary    (solid accent CTA)
 *   secondary -> secondary  (outline accent)
 *   ghost     -> ghost      (transparent)
 *   danger    -> danger     (solid danger, destructive confirms)
 */

import type React from 'react';
import { forwardRef } from 'react';
import { buttonVariants } from '@heroui/react';
import { cn } from '../../lib/cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly loading?: boolean;
  readonly fullWidth?: boolean;
  readonly icon?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, fullWidth = false, icon, disabled, className, children, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      {...rest}
    >
      {loading ? <Spinner size="sm" /> : icon}
      {children}
    </button>
  );
});
